import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { of, switchMap } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { Menu } from 'primeng/menu';
import { User } from '@core/models';
import {
  ApplicationPropertyService,
  UserService,
  V1_NAVIGATION,
  V1_USER_PROFILE_URL
} from '@core/services';

/**
 * Route to the nymbl2 tasks page. An in-app route rather than a URL, so the click
 * navigates through the router and the surrounding chrome survives it.
 */
export const TASKS_ROUTE = '/productivity/tasks';

/**
 * The class PrimeNG's Menu puts on a routerLink row whose route is open (via
 * `routerLinkActive`). Reused for the legacy Profile row, which has no routerLink to drive it,
 * so both kinds of row are styled by the one rule in this component's CSS.
 */
export const ACTIVE_LINK_CLASS = 'p-menu-item-link-active';

/**
 * Explicit element ids for the control and each overlay item.
 *
 * Named and stable on purpose: without them the only handles are PrimeNG's generated
 * `p-menu-N_0` indices, which shift whenever an optional item drops out, or the items'
 * visible labels, which are user-facing copy. Both make brittle e2e locators. PrimeNG
 * honors `MenuItem.id` as the rendered element id (see Menu#menuitemId), so these are the
 * ids that actually reach the DOM.
 */
export const PROFILE_MENU_IDS = {
  trigger: 'profile-menu',
  overlay: 'profile-menu-overlay',
  profile: 'profile-menu-profile',
  tasks: 'profile-menu-tasks',
  buildVersion: 'profile-menu-build-version',
  logout: 'profile-menu-logout'
} as const;

/**
 * The user identity control in the topbar: profile photo, name, and an overlay holding
 * Profile, Tasks, the build version, and Log Out.
 *
 * Deliberately not a dumb component. Everything it shows is derived from a service the
 * topbar has no reason to know about — the photo bytes, the build version property, the
 * deployment-specific v1 profile URL — so pushing those up would spread one feature
 * across two components for no gain.
 *
 * Log Out reuses the `logout=true` query param rather than calling AuthService directly.
 * authGuard owns the whole sequence (clear tokens, then redirect to the v1 login page
 * with a return path); calling the service alone would clear the session and leave the
 * user sitting on a dead page.
 */
@Component({
  selector: 'app-user-profile-menu',
  templateUrl: 'user-profile-menu.component.html',
  styleUrls: ['user-profile-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Unscoped so the stylesheet can color p-menu's rows to match the sidebar navigation
  // menu. Emulated encapsulation cannot: the overlay is appended to <body>, and its rows
  // come from PrimeNG's own template, so neither carries this component's scoping
  // attribute. Every rule is manually scoped — see the stylesheet header.
  encapsulation: ViewEncapsulation.None,
  host: { class: 'app-user-profile-menu-host' },
  imports: [Avatar, Menu]
})
export class UserProfileMenuComponent {
  private userService = inject(UserService);
  private applicationPropertyService = inject(ApplicationPropertyService);
  private v1Navigation = inject(V1_NAVIGATION);

  readonly user = input.required<User>();

  /** Exposed for the template; consumers locate against PROFILE_MENU_IDS itself. */
  protected readonly ids = PROFILE_MENU_IDS;

  private menu = viewChild<Menu>('menu');
  private trigger =
    viewChild<ElementRef<HTMLButtonElement>>('profileMenuTrigger');

  /** Drives aria-expanded and the chevron direction. */
  protected readonly open = signal(false);

  /**
   * Falls back through the name parts so a user missing one still gets a stable label
   * rather than a stray space or the literal "undefined".
   */
  protected readonly displayName = computed(() => {
    const user = this.user();
    const fullName = [user?.firstName, user?.lastName]
      .filter((part) => !!part)
      .join(' ')
      .trim();

    return fullName || user?.username || '';
  });

  /**
   * The button's accessible name. The visible name text is hidden below 991px, which
   * removes it from the accessibility tree, so the label cannot be left implicit. It
   * still contains the visible text when that is showing, so the two do not conflict.
   */
  protected readonly triggerLabel = computed(
    () => this.displayName() || 'User menu'
  );

  private readonly fetchedPhoto = toSignal(
    toObservable(computed(() => this.user()?.id)).pipe(
      switchMap((id) =>
        id ? this.userService.getUserPhotoDataUrl(id) : of(null)
      )
    ),
    { initialValue: null }
  );

  /**
   * Set when the browser rejects bytes the request itself delivered successfully, so a
   * corrupt upload degrades to the fallback icon instead of a broken image. Linked to the
   * fetched photo so it resets whenever a different one arrives, rather than latching for
   * the rest of the session.
   */
  private readonly photoFailed = linkedSignal({
    source: this.fetchedPhoto,
    computation: () => false
  });

  /**
   * Null covers every case where no photo can be shown — no upload on disk (204), a
   * failed request, undecodable bytes, and the window before the request resolves —
   * because the fallback icon is the answer to all four.
   */
  protected readonly photoSrc = computed(() =>
    this.photoFailed() ? null : this.fetchedPhoto()
  );

  private readonly buildVersion = toSignal(
    this.applicationPropertyService.appBuildVersion$,
    { initialValue: null }
  );

  /**
   * Built as one array so the separator stays anchored immediately above Log Out however
   * many optional items dropped out. Two can be absent: Profile when the active
   * deployment cannot resolve a v1 URL, and the build version whenever its request
   * failed or has not resolved.
   */
  protected readonly menuItems = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [];
    const profileHref = this.v1Navigation.toHref(V1_USER_PROFILE_URL);

    if (profileHref) {
      items.push({
        id: PROFILE_MENU_IDS.profile,
        label: 'Profile',
        icon: 'pi pi-user',
        linkClass: this.v1Navigation.isActive(V1_USER_PROFILE_URL)
          ? ACTIVE_LINK_CLASS
          : undefined,
        command: () => this.v1Navigation.navigate(V1_USER_PROFILE_URL)
      });
    }

    items.push({
      id: PROFILE_MENU_IDS.tasks,
      label: 'Tasks',
      icon: 'pi pi-check-square',
      routerLink: TASKS_ROUTE
    });

    const version = this.buildVersion();
    if (version) {
      items.push({
        id: PROFILE_MENU_IDS.buildVersion,
        label: version,
        icon: 'pi pi-book',
        // Disabled is what makes it non-interactive: PrimeNG skips disabled items during
        // keyboard traversal, and its click handler returns before hiding the overlay.
        disabled: true
      });
    }

    items.push(
      { separator: true },
      {
        id: PROFILE_MENU_IDS.logout,
        label: 'Log Out',
        icon: 'pi pi-sign-out',
        routerLink: [],
        queryParams: { logout: true }
      }
    );

    return items;
  });

  protected toggle(event: Event): void {
    this.menu()?.toggle(event);
  }

  protected onPhotoError(): void {
    this.photoFailed.set(true);
  }

  protected onShow(): void {
    this.open.set(true);
  }

  /**
   * PrimeNG restores focus itself when Escape closes the overlay, but not when an outside
   * click or a second click on the trigger does. Restoring it here keeps a keyboard user
   * from being dumped at the top of the document, so the trigger can just be activated
   * again.
   */
  protected onHide(): void {
    this.open.set(false);
    this.trigger()?.nativeElement.focus();
  }
}
